import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import OutletSellTable from "../../ProductLevelChallanData/components/OutletSellTable";
import ProductDataTable from "./ProductDataTable";
import { Icon } from "./SvgIcons";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import ButtonComponent from "../../../components/Comman/ButtonComponent";
import { grayShadeLightColor, greyStrongColor, lightWhiteRedShade, stoneColdGrayColor, strongBlack, strongBlue, white, whiteBlueShade } from "../../../config/ColorObj";

// ─── Styled Dialog ────────────────────────────────────────────────────────────
const BootstrapDialog = styled(Dialog)(() => ({
    "& .MuiDialog-paper": {
        borderRadius: "12px",
        overflow: "hidden",
    },
}));



// ─── Dialog Header ────────────────────────────────────────────────────────────
const DialogHeader = ({ challanId, onClose }) => (
    <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
            <Box />
            <TypographyCompo style={styles.headerTitle}>Challan Details</TypographyCompo>
            {challanId && (
                <TypographyCompo style={styles.headerBadge}>{challanId}</TypographyCompo>
            )}
        </Box>
        <ButtonComponent onClick={onClose} style={styles.closeBtn} aria-label="Close">
            <CloseIcon sx={{ fontSize: 16 }} />
        </ButtonComponent>
    </Box>
);

DialogHeader.propTypes = {
    challanId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
const TAB_CONFIG = [
    { label: "Outlet Wise", icon: "outlet" },
    { label: "Product Wise", icon: "product" },
];

const TabBar = ({ activeTab, onChange }) => (
    <Box sx={styles.tabBar}>
        {TAB_CONFIG.map(({ label, icon }, i) => (
            <ButtonComponent
                key={label}
                onClick={() => onChange(i)}
                style={{
                    ...styles.tabBtn,
                    ...(activeTab === i ? styles.tabBtnActive : styles.tabBtnInactive),
                }}
            >
                <TypographyCompo sx={styles.tabContent}>
                    <Icon type={icon} size={16} />
                    {label}
                </TypographyCompo>
            </ButtonComponent>
        ))}
    </Box>
);

TabBar.propTypes = {
    activeTab: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
};

// ─── Summary Strip ────────────────────────────────────────────────────────────
const SummaryStrip = ({ metrics }) => (
    <Box sx={styles.summaryStrip}>
        {metrics.map(({ label, value, icon }) => (
            <Box key={label} sx={styles.metricCard}>
                <Box sx={styles.metricHeader}>
                    <Icon type={icon} size={14} color={strongBlue} />
                    <TypographyCompo style={styles.metricLabel}>{label}</TypographyCompo>
                </Box>
                <TypographyCompo style={styles.metricValue}>{value}</TypographyCompo>
            </Box>
        ))}
    </Box>
)

SummaryStrip.propTypes = {
    metrics: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })
    ).isRequired,
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ChallanDataPopUp = ({ handleCloses, open, challanId = "CHN-2026-04" }) => {
    const [activeTab, setActiveTab] = useState(0);

    // Keep data outside render or pass via props in real usage
    const outletSellData = useMemo(() => [
        { outletName: "City Mart", date: "12-04-2026", sellQty: 4 },
        { outletName: "Quick Shop", date: "13-04-2026", sellQty: 3 },
        { outletName: "Daily Needs", date: "14-04-2026", sellQty: 3 },
    ], []);

    const productSellData = useMemo(() => [
        { productName: "Parle-G", sellQty: 7 },
        { productName: "Good Day", sellQty: 3 },
    ], []);

    const outletMetrics = useMemo(() => [
        { label: "Total Outlets", value: outletSellData.length, icon: "outlet" },
        { label: "Total Qty Sold", value: outletSellData.reduce((s, r) => s + r.sellQty, 0), icon: "qty" },
        {
            label: "Date Range",
            value: `${outletSellData[0]?.date + "   "}   To   ${"   " + outletSellData.at(-1)?.date}`,
            icon: "calendar",
        },
    ], [outletSellData]);

    const productMetrics = useMemo(() => {
        const total = productSellData.reduce((s, r) => s + r.sellQty, 0);
        const top = productSellData.reduce((a, b) => (b.sellQty > a.sellQty ? b : a), productSellData[0]);

        return [
            { label: "Total Products", value: productSellData.length, icon: "product" },
            { label: "Total Qty Sold", value: total, icon: "qty" },
            { label: "Top Product", value: top?.productName ?? "—", icon: "top" },
        ];
    }, [productSellData]);

    return (
        <BootstrapDialog open={open} fullWidth maxWidth="md" onClose={handleCloses}>
            <DialogHeader challanId={challanId} onClose={handleCloses} />

            <TabBar activeTab={activeTab} onChange={setActiveTab} />

            <DialogContent sx={styles.dialogContent}>
                <SummaryStrip metrics={activeTab === 0 ? outletMetrics : productMetrics} />

                {activeTab === 0 && <OutletSellTable rows={outletSellData} />}
                {activeTab === 1 && <ProductDataTable rows={productSellData} />}
            </DialogContent>

        </BootstrapDialog>
    );
};

ChallanDataPopUp.propTypes = {
    handleCloses: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    challanId: PropTypes.string,
};

export default ChallanDataPopUp;

const styles = {
    // Header
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        borderBottom: "0.5px solid",
        borderColor: "divider",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: 1.25,
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: "inherit",
    },
    headerBadge: {
        fontSize: 11,
        fontWeight: 500,
        padding: "3px 9px",
        borderRadius: 20,
        background: whiteBlueShade,
        color: strongBlue,
        letterSpacing: "0.3px",
    },
    closeBtn: {
        width: 28,
        height: 28,
        border: "0.5px solid #ccc",
        borderRadius: 6,
        background: "transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: grayShadeLightColor,
    },

    // Tabs
    tabBar: {
        display: "flex",
        px: 3,
        borderBottom: "0.5px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
    },
    tabBtn: {
        fontSize: 13,
        padding: "10px 16px",
        border: "none",
        cursor: "pointer",
        background: "transparent",
        letterSpacing: "0.2px",
        transition: "color 0.15s",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    tabBtnActive: {
        borderBottom: `2px solid ${strongBlue}`,
        color: strongBlue,
        fontWeight: 600,
    },
    tabBtnInactive: {
        borderBottom: "2px solid transparent",
        color: grayShadeLightColor,
        fontWeight: 400,
        ":hover": {
            color: strongBlack,
        },
    },

    // Content
    dialogContent: {
        p: 3,
        backgroundColor: lightWhiteRedShade,
    },

    // Summary strip
    summaryStrip: {
        display: "flex",
        gap: 1.5,
        mb: 2.5,
    },
    metricCard: {
        flex: 1,
        backgroundColor: "background.paper",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: "10px 14px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        transition: "all 0.2s ease",
        cursor: "default",
        ":hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            transform: "translateY(-1px)",
        },
    },
    metricLabel: {
        fontSize: 12,
        color: greyStrongColor,
        fontWeight: 500,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    },
    metricValue: {
        fontSize: 16,
        fontWeight: 600,
        color: "inherit",
    },

    // Footer
    footer: {
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        px: 3,
        py: 1.75,
        borderTop: "0.5px solid",
        borderColor: "divider",
    },
    exportBtn: {
        fontSize: 13,
        padding: "7px 18px",
        border: "0.5px solid #ccc",
        borderRadius: 6,
        background: "transparent",
        cursor: "pointer",
        color: stoneColdGrayColor,
    },
    primaryBtn: {
        fontSize: 13,
        padding: "7px 18px",
        border: "none",
        borderRadius: 6,
        background: strongBlue,
        cursor: "pointer",
        color: white,
        fontWeight: 600,
    },
    tabContent: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    metricHeader: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
};